import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import "styles.css";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import Drawer from "@mui/material/Drawer";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDDropdownCheckbox from "components/MDDropdownCheckbox";
import MDInput from "components/MDInput";
import MDTextInputChip from "components/MDTextInputChip";
import MDTypography from "components/MDTypography";

import MDComplaintRequirementsStepper from "components/MDComplaintRequirementsStepper";
import MDImageUpload from "components/MDImageUpload";
import MDProductRequirementsStepper from "components/MDProductRequirementsStepper";
import MDSelect from "components/MDSelect";
import ConfiguratorRoot from "examples/Configurator/ConfiguratorRoot";
// import { useScrollLock } from "./hook";
import MKDatePicker from "components/MKDatePicker";

const MDActionConfigurator = forwardRef(
  (
    { title, subtitle, productAmount = 0, onCompleteConfig, data, state = "" },
    ref
  ) => {
    // const { lockScroll, unlockScroll } = useScrollLock();
    const [openConfigurator, setOpenConfigurator] = useState(false);
    const [open, setOpen] = useState(false);
    const [inputs, setInputs] = useState(data?.map((item) => ({ ...item })));

    useEffect(() => {
      setInputs(data?.map((item) => ({ ...item })));
    }, [data]);

    const handleToggleConfigurator = () => {
      setOpenConfigurator(!openConfigurator);
      setOpen(!open);

      // Toggle the body scroll lock class
      //   if (!openConfigurator) {
      //     // document.body.classList.add("body-scroll-lock"); // Lock scrolling
      //     lockScroll();
      //   } else {
      //     // document.body.classList.remove("body-scroll-lock"); // Unlock scrolling
      //     unlockScroll();
      //   }
    };

    // Expose the child function to the parent component through the ref
    useImperativeHandle(ref, () => ({
      handleToggleConfigurator,
    }));

    const getGrandTotal = () => {
      let total = 0;
      inputs?.forEach((input) => {
        if (input?.keyType === "addons") {
          const addonsTotal = input?.valueType?.reduce((acc, item) => {
            return acc + item.addonAmount;
          }, 0);
          total = parseInt(addonsTotal) + parseInt(productAmount);
        }
      });

      return total;
    };

    const changeHandler = (e) => {
      setInputs((prevInputs) => {
        return prevInputs.map((val) => {
          if (val.keyType === e.target.name) {
            return { ...val, valueType: e.target.value };
          }
          return val;
        });
      });
    };

    const handleArrayChange = (e) => {
      console.log("🚀 ~ file: index.js:93 ~ handleArrayChange ~ e:", e);
      const grandTotal = getGrandTotal();

      setInputs((prevInputs) => {
        return prevInputs.map((val) => {
          if (val.keyType === e.name) {
            // check name
            return { ...val, valueType: e.value }; //check value
          }
          if (val.keyType === "computedAmount") {
            return { ...val, valueType: grandTotal };
          }
          return val;
        });
      });
    };

    const getClientForRequirements = () => {
      let client;
      inputs?.map((input) => {
        if (input?.keyType === "requestedBy" || input?.keyType === "client") {
          client = input?.valueType;
        }
      });
      return client;
    };

    const submitHandler = async (e) => {
      e.preventDefault();

      let dataObj = {};
      inputs?.forEach((item) => {
        dataObj = { ...dataObj, [item.keyType]: item.valueType };
      });
      console.log(
        "🚀 ~ file: index.js:123 ~ submitHandler ~ dataObj:",
        dataObj
      );
      onCompleteConfig(dataObj);

      return () => {
        setInputs([]);
      };
    };

    return (
      <Drawer anchor="right" open={open}>
        <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }}>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="baseline"
            pt={4}
            pb={0.5}
            px={3}
          >
            <MDBox>
              <MDTypography variant="body2" color="text">
                Vertical Lakes Configurator
              </MDTypography>
            </MDBox>

            <Icon
              sx={({ typography: { size }, palette: { dark, white } }) => ({
                fontSize: `${size.lg} !important`,
                color: dark.main,
                stroke: "currentColor",
                strokeWidth: "2px",
                cursor: "pointer",
                transform: "translateY(5px)",
              })}
              onClick={() => handleToggleConfigurator()}
            >
              close
            </Icon>
          </MDBox>
          <Divider />
          <MDBox>
            <Card>
              <MDBox
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                mx={2}
                mt={-3}
                p={2}
                mb={1}
                textAlign="center"
                sx={{ whiteSpace: "normal" }}
              >
                <MDTypography
                  variant="h6"
                  fontWeight="medium"
                  color="white"
                  mt={1}
                >
                  {state === "edit" ? "Edit " : "Create "} {title}
                </MDTypography>
                <MDBox sx={{ whiteSpace: "normal" }}>
                  <MDTypography
                    variant="body2"
                    fontWeight="small"
                    color="white"
                    mt={1}
                  >
                    {state === "edit" ? "Edit " : "Create "} {subtitle}
                  </MDTypography>
                </MDBox>
              </MDBox>

              <MDBox pt={4} pb={3} px={3}>
                <MDBox
                  component="form"
                  role="form"
                  method="POST"
                  onSubmit={submitHandler}
                >
                  {inputs?.map((item, index) => (
                    <MDBox key={index}>
                      {item?.dataType === "productrequirement" && (
                        <MDBox mb={2} pt={4} pb={3}>
                          <MDProductRequirementsStepper
                            data={item}
                            client={getClientForRequirements()}
                            onRequirement={handleArrayChange}
                          />
                        </MDBox>
                      )}
                      {item?.dataType === "complaintrequirement" && (
                        <MDBox mb={2} pt={4} pb={3}>
                          <MDComplaintRequirementsStepper
                            data={item}
                            client={getClientForRequirements()}
                            onRequirement={handleArrayChange}
                          />
                        </MDBox>
                      )}
                      {item?.dataType === "disabled" && (
                        <MDBox mb={2}>
                          <MDInput
                            type="text"
                            label={item?.labelType}
                            placeholder={item?.placeholderType}
                            fullWidth
                            success
                            disabled
                            focused
                            name={item?.keyType}
                            value={item?.valueType}
                            onChange={changeHandler}
                          />
                        </MDBox>
                      )}

                      {item?.dataType === "textarea" && (
                        <MDBox mb={2}>
                          <MDInput
                            type="text"
                            multiline
                            rows={3}
                            label={
                              state === "edit"
                                ? `Update ${item?.labelType}`
                                : `New ${item?.labelType}`
                            }
                            placeholder={item?.placeholderType}
                            fullWidth
                            name={item?.keyType}
                            value={item?.valueType}
                            onChange={changeHandler}
                          />
                        </MDBox>
                      )}

                      {item?.dataType === "text" && (
                        <MDBox mb={2}>
                          <MDInput
                            type="text"
                            label={
                              state === "edit"
                                ? `Update ${item?.labelType}`
                                : `New ${item?.labelType}`
                            }
                            placeholder={item?.placeholderType}
                            fullWidth
                            name={item?.keyType}
                            value={item?.valueType}
                            onChange={changeHandler}
                          />
                        </MDBox>
                      )}

                      {item?.dataType === "email" && (
                        <MDBox mb={2}>
                          <MDInput
                            type="email"
                            label={
                              state === "edit"
                                ? `Update ${item?.labelType}`
                                : `New ${item?.labelType}`
                            }
                            placeholder={item?.placeholderType}
                            fullWidth
                            name={item?.keyType}
                            value={item?.valueType}
                            onChange={changeHandler}
                          />
                        </MDBox>
                      )}

                      {item?.dataType === "phone" && (
                        <MDBox mb={2}>
                          <MDInput
                            type="phone"
                            label={
                              state === "edit"
                                ? `Update ${item?.labelType}`
                                : `New ${item?.labelType}`
                            }
                            placeholder={item?.placeholderType}
                            fullWidth
                            name={item?.keyType}
                            value={item?.valueType}
                            onChange={changeHandler}
                          />
                        </MDBox>
                      )}

                      {item?.dataType === "number" && (
                        <MDBox mb={2}>
                          <MDInput
                            type="number"
                            label={
                              state === "edit"
                                ? `Update ${item?.labelType}`
                                : `New ${item?.labelType}`
                            }
                            fullWidth
                            name={item?.keyType}
                            value={item?.valueType}
                            onChange={changeHandler}
                          />
                        </MDBox>
                      )}

                      {item?.dataType === "select" && (
                        <MDBox mb={2}>
                          <MDSelect
                            type="dropdown"
                            label={
                              state === "edit"
                                ? `Update ${item?.labelType}`
                                : `New ${item?.labelType}`
                            }
                            placeholder={item?.placeholderType}
                            fullWidth
                            options={item?.options}
                            name={item?.keyType}
                            itemValue={item?.valueType}
                            onSelectedNamesChange={handleArrayChange}
                          />
                        </MDBox>
                      )}

                      {item?.dataType === "dropdowncheckbox" && (
                        <MDBox mb={2}>
                          <MDDropdownCheckbox
                            type="dropdown"
                            label={
                              state === "edit"
                                ? `Update ${item?.labelType}`
                                : `New ${item?.labelType}`
                            }
                            fullWidth
                            options={item?.options}
                            name={item?.keyType}
                            arrayValue={item?.valueType}
                            onSelectedNamesChange={handleArrayChange}
                          />
                        </MDBox>
                      )}

                      {item?.dataType === "textinputchip" && (
                        <MDBox mb={2}>
                          <MDTextInputChip
                            type="text"
                            label={
                              state === "edit"
                                ? `Update ${item?.labelType}`
                                : `New ${item?.labelType}`
                            }
                            fullWidth
                            options={item?.options}
                            name={item?.keyType}
                            value={item?.valueType}
                            onTextInputChipChange={handleArrayChange}
                          />
                        </MDBox>
                      )}

                      {item?.dataType === "image" && (
                        <MDBox mb={2}>
                          <MDImageUpload
                            title={
                              state === "edit"
                                ? `Update ${item?.labelType}`
                                : `New ${item?.labelType}`
                            }
                            valueType={item.valueType}
                            name={item?.keyType}
                            onImageChange={handleArrayChange}
                          />
                        </MDBox>
                      )}
                      {item?.dataType === "date" && (
                        <MDBox mb={2}>
                          <MKDatePicker
                            label={
                              state === "edit"
                                ? `Update ${item?.labelType}`
                                : `New ${item?.labelType}`
                            }
                            valueType={item.valueType}
                            name={item?.keyType}
                            onDateChange={handleArrayChange}
                          />
                        </MDBox>
                      )}
                      {item?.keyType === "computedAmount" && (
                        <MDBox mb={2}>
                          <MDBox textAlign="flex-end" mr={5}>
                            <MDBox
                              display="flex"
                              flexDirection="row"
                              pb={1}
                              justifyContent="flex-end"
                            >
                              <MDTypography
                                variant="body2"
                                fontWeight="medium"
                                color="text"
                              >
                                Product:{" "}
                              </MDTypography>
                              <MDTypography variant="h6" pl={3}>
                                Ksh. {productAmount}
                              </MDTypography>
                            </MDBox>
                            <MDBox
                              display="flex"
                              flexDirection="row"
                              pb={1}
                              justifyContent="flex-end"
                            >
                              <MDBox display="flex" flexDirection="column">
                                {inputs?.map((input) => (
                                  <>
                                    {input?.keyType === "addons" && (
                                      <>
                                        {input?.valueType?.map(
                                          (addon, index) => (
                                            <MDBox
                                              display="flex"
                                              flexDirection="row"
                                              justifyContent="flex-end"
                                            >
                                              <MDTypography
                                                variant="body2"
                                                color="text"
                                              >
                                                {addon.name} {": "}
                                              </MDTypography>
                                              <MDTypography
                                                key={index}
                                                variant="h6"
                                                pl={1}
                                              >
                                                Ksh. {addon?.addonAmount}
                                              </MDTypography>
                                            </MDBox>
                                          )
                                        )}
                                      </>
                                    )}
                                  </>
                                ))}
                              </MDBox>
                            </MDBox>
                            <MDBox
                              display="flex"
                              flexDirection="row"
                              py={1}
                              justifyContent="flex-end"
                            >
                              <MDTypography
                                variant="body2"
                                fontWeight="medium"
                                color="text"
                              >
                                Total + Addons:{" "}
                              </MDTypography>
                              <MDTypography variant="h5" pl={3}>
                                Ksh. {getGrandTotal()}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                        </MDBox>
                      )}
                    </MDBox>
                  ))}
                  <MDBox mt={4} mb={1}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      fullWidth
                      type="submit"
                    >
                      {state === "edit" ? "Update " : "Create "} {title}
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>
        </ConfiguratorRoot>
      </Drawer>
    );
  }
);

export default MDActionConfigurator;
